import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import en from './locales/en.json'
import { safeStorage } from '../lib/safe-storage'

export const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'hi', name: 'हिन्दी' },
  { code: 'mr', name: 'मराठी' },
  { code: 'bn', name: 'বাংলা' },
  { code: 'te', name: 'తెలుగు' },
  { code: 'ta', name: 'தமிழ்' },
  { code: 'kn', name: 'ಕನ್ನಡ' },
  { code: 'gu', name: 'ગુજરાતી' }
] as const

export const LANG_STORAGE_KEY = 'staff_lang'

const localeLoaders: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  hi: () => import('./locales/hi.json'),
  mr: () => import('./locales/mr.json'),
  bn: () => import('./locales/bn.json'),
  te: () => import('./locales/te.json'),
  ta: () => import('./locales/ta.json'),
  kn: () => import('./locales/kn.json'),
  gu: () => import('./locales/gu.json')
}

const loadedLocales = new Set<string>(['en'])

/**
 * Loads a locale bundle on demand. Never throws: a failed chunk fetch (weak
 * signal, stale service-worker cache) leaves the UI on English rather than
 * taking the caller down with it.
 */
export async function ensureLocaleLoaded (lng: string): Promise<boolean> {
  if (lng === 'en' || loadedLocales.has(lng)) return true

  const load = localeLoaders[lng]
  if (!load) return false

  try {
    const mod = await load()
    i18n.addResourceBundle(lng, 'translation', mod.default, true, true)
    loadedLocales.add(lng)
    return true
  } catch (err) {
    console.error(`[i18n] failed to load locale "${lng}"; staying on English`, err)
    return false
  }
}

const savedLang = safeStorage.get(LANG_STORAGE_KEY, 'en') || 'en'

/**
 * Initialised synchronously with English only.
 *
 * This module previously used a top-level `await` to preload the saved locale.
 * That made every importer — including `StaffApp` — depend on a network chunk
 * fetch at module-evaluation time, so one failed request meant the staff app
 * never rendered at all. English is bundled; anything else streams in after.
 */
i18n.use(initReactI18next).init({
  resources: { en: { translation: en as Record<string, unknown> } },
  lng: 'en',
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
  react: {
    // Never suspend on a missing bundle — render the English fallback instead.
    useSuspense: false
  }
})

/**
 * Switches to the saved locale once the bundle arrives. Fire-and-forget: the
 * app is already interactive in English by the time this resolves.
 */
export function applySavedLocale (): void {
  if (savedLang === 'en') return
  void ensureLocaleLoaded(savedLang).then(ok => {
    if (ok) void i18n.changeLanguage(savedLang)
  })
}

/** Switches language and persists the choice. Resolves to the language in effect. */
export async function setLanguage (code: string): Promise<string> {
  const ok = await ensureLocaleLoaded(code)
  const effective = ok ? code : 'en'
  await i18n.changeLanguage(effective)
  if (ok) safeStorage.set(LANG_STORAGE_KEY, code)
  return effective
}

applySavedLocale()

export default i18n
